import {
  InformationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import type { GeographicDataQuality as DataQuality } from '@/services/analytics.service';

interface GeographicDataQualityProps {
  data: DataQuality;
}

const GeographicDataQualityFooter: React.FC<GeographicDataQualityProps> = ({ data }) => {
  const locationCoverage =
    data.totalIncidents > 0
      ? Math.round((data.incidentsWithLocation / data.totalIncidents) * 100)
      : 0;

  const counters = [
    { label: 'Total Incidents', value: data.totalIncidents },
    { label: 'With Location', value: data.incidentsWithLocation },
    { label: 'Missing Location', value: data.incidentsWithoutLocation },
    { label: 'EP Activities', value: data.epActivitiesTotal },
    { label: 'EP Venues', value: data.epVenuesTotal },
    { label: 'Geofences', value: data.epGeofencesTotal },
  ];

  return (
    <div className="rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50 p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-secondary-700 dark:text-secondary-300">
        <InformationCircleIcon className="w-4 h-4" />
        Data Quality
      </div>

      {/* Counters row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {counters.map(({ label, value }) => (
          <div key={label} className="text-center">
            <div className="text-lg font-semibold text-secondary-900 dark:text-white">
              {value.toLocaleString()}
            </div>
            <div className="text-xs text-secondary-500 dark:text-secondary-400">
              {label}
            </div>
          </div>
        ))}
      </div>

      {/* Coverage bar */}
      {data.totalIncidents > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-secondary-500 dark:text-secondary-400">
            <span>Location coverage</span>
            <span>{locationCoverage}% of incidents have a location</span>
          </div>
          <div className="w-full h-2 bg-secondary-200 dark:bg-secondary-700 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${locationCoverage}%`,
                backgroundColor: locationCoverage >= 80 ? '#10B981' : locationCoverage >= 50 ? '#F59E0B' : '#EF4444',
              }}
            />
          </div>
        </div>
      )}

      {/* Alias warnings */}
      {data.locationAliasWarnings.length > 0 && (
        <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-md p-2">
          <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-medium">Location aliases merged: </span>
            {data.locationAliasWarnings.map((w, i) => (
              <span key={w.canonical}>
                {i > 0 && '; '}
                {w.variants.join(', ')} → {w.canonical}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GeographicDataQualityFooter;
